import { Box, Typography, Tooltip, useTheme } from "@mui/material";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { BsClock } from "react-icons/bs";
import { useTranslation } from "react-i18next";
import { FiPlus, FiMinus } from "react-icons/fi";
import { LuCheck, LuDownload, LuFilter, LuFileText, LuCalendar } from "react-icons/lu";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import AgGrid from "../../../components/grid/AgGrid";
import GridSelectEditor from "../../../components/grid/GridSelectEditor";
import GridDateEditor from "../../../components/grid/GridDateEditor";
import { defaultColDef, leftAlignColDef, rightAlignColDef, centerAlignColDef, floatFormater } from "../../../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { Corporate } from "../../../db_access/corporate";
import { Structure } from "../../../db_access/structure";
import { Supply_chain } from "../../../db_access/supply_chain";
import { scope1Categories, scope2Categories, scope3Categories } from "../ScopeCategory";
import { DataTableCard, SegmentedButton, M3Button, M3IconButton, Chip, M3Badge, SplitButton } from "../../../components/m3";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import OrganizationDocumentUpload from "../OrganizationDocumentUpload";
import EvidenceBillBatchAnalyzerModal from "../../../components/dialog/EvidenceBillBatchAnalyzerModal";

const CheckboxRenderer = (params) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const active = !!params.value;

  const handleClick = (e) => {
    e.stopPropagation();
    params.node.setDataValue(params.colDef.field, active ? 0 : 1);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        cursor: "pointer",
      }}
    >
      {active ? (
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            backgroundColor: "#659d33",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: "0 2px 4px rgba(101, 157, 51, 0.2)"
          }}
        >
          <LuCheck size={14} color="#ffffff" strokeWidth={3} />
        </Box>
      ) : (
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
            flexShrink: 0,
          }}
        />
      )}
    </Box>
  );
};

const includesCategory = (categories, cat) => {
  if (!cat) return false;
  const normalized = cat.toLowerCase().trim();
  const stripped = normalized.replace(/^\d+\.\s*/, "").trim();
  return categories.some((c) => {
    const cNorm = c.toLowerCase().trim();
    const cStrip = cNorm.replace(/^\d+\.\s*/, "").trim();
    return cNorm === normalized || cStrip === stripped;
  });
};

const getScopeLabel = (row) => {
  let category = row?.category;
  if (category && typeof category === "object") {
    category = category.name || category.label || category.category || "";
  }
  if (!category) return "Unknown";
  const catLower = String(category).toLowerCase().trim();
  if (row?.is_avoided || row?.is_avoided === 1) return "Avoided";
  if (row?.is_biogenic || row?.is_biogenic === 1) return "Biogenic";
  if (includesCategory(scope1Categories, category)) return "Scope 1";
  if (includesCategory(scope2Categories, category) || catLower.includes("location") || catLower.includes("market") || catLower.includes("electricity") || catLower.includes("steam")) return "Scope 2";
  if (includesCategory(scope3Categories, category) || catLower.includes("purchased goods") || catLower.includes("capital goods") || catLower.includes("business travel") || catLower.includes("employee commuting") || catLower.includes("transportation") || catLower.includes("waste")) return "Scope 3";
  return "Unknown";
};

const SCOPE_TONE_MAP = {
  Avoided: "primary",
  Biogenic: "tertiary",
  "Scope 1": "error",
  "Scope 2": "warning",
  "Scope 3": "info",
};

const ScopeBadgeRenderer = (params) => {
  const label = getScopeLabel(params.data);
  const tone = SCOPE_TONE_MAP[label] || "neutral";

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <M3Badge tone={tone}>{label}</M3Badge>
    </Box>
  );
};

const M3DateCellRenderer = (params) => {
  const val = params.value;
  const isStart = params.colDef?.field === "billing_period_start";
  const isEditable = Boolean(params.colDef?.editable);

  const handleClick = (e) => {
    if (!isEditable) return;
    params.api?.startEditingCell({
      rowIndex: params.node.rowIndex,
      colKey: params.colDef.field,
    });
  };

  if (!val || val === "N/A" || val === "None" || val === "-") {
    return (
      <Box
        onClick={handleClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.6,
          color: "var(--m3-outline, #94a3b8)",
          fontSize: "12px",
          fontStyle: "italic",
          cursor: isEditable ? "pointer" : "default",
          py: 0.25,
          px: 0.75,
          borderRadius: "6px",
          transition: "all 0.15s ease",
          "&:hover": isEditable
            ? {
                backgroundColor: "rgba(22, 163, 74, 0.08)",
                color: "#16a34a",
              }
            : {},
        }}
        title={isEditable ? "Click to set date" : undefined}
      >
        <LuCalendar size={13} style={{ opacity: 0.6 }} />
        <span>{isStart ? "No date data available" : "-"}</span>
      </Box>
    );
  }

  const cleanStr = String(val).replace(/\s+00:00:00.*$/, "").trim();
  const d = dayjs(cleanStr);
  const displayDate = d.isValid() ? d.format("YYYY-MM-DD") : cleanStr;

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.6,
        fontSize: "13px",
        fontFamily: "Poppins, sans-serif",
        fontWeight: 500,
        color: "inherit",
        cursor: isEditable ? "pointer" : "default",
        py: 0.25,
        px: 0.75,
        borderRadius: "6px",
        transition: "all 0.15s ease",
        "&:hover": isEditable
          ? {
                backgroundColor: "rgba(22, 163, 74, 0.08)",
                color: "#16a34a",
              }
          : {},
      }}
      title={isEditable ? "Click to change date" : undefined}
    >
      <LuCalendar size={13} color="#16a34a" />
      <span>{displayDate}</span>
    </Box>
  );
};

const sortByCategoryNumber = (arr) =>
  [...arr].sort((a, b) => {
    const aCat = typeof a.category === "object" ? (a.category?.name || "") : (a.category || "");
    const bCat = typeof b.category === "object" ? (b.category?.name || "") : (b.category || "");
    return (parseInt(aCat) || 9999) - (parseInt(bCat) || 9999);
  });

const PAGE_STEPS = [5, 10, 15];

const EmissionGrid = ({ organizationType, organizationID, emissions, emissionCategories, selectedEmissionID, setSelectedEmissionIDs, selectedTab, setSelectedTab, tabs, inventoryTotalFossil = 0, inventoryTotalBiogenic = 0, hasPendingDetailEdits = false, saveRef, readOnly = false, validationStatus = "Not Validated" }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { columnVisibilities, setRefresh, setIsSaveClicked, setIsSaveVisible, enqueueAlert } = useContextProvider();
  const { t } = useTranslation();
  const [localEmissions, setLocalEmissions] = useState(emissions);
  const [selectedRowIDs, setSelectedRowIDs] = useState([]);
  const [emissionIdsToBeDeleted, setEmissionIdsToBeDeleted] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [batchAnalyzerOpen, setBatchAnalyzerOpen] = useState(false);

  const selectedEmission = useMemo(
    () => localEmissions.find((e) => e.id === (selectedRowIDs[0] || selectedEmissionID)),
    [localEmissions, selectedRowIDs, selectedEmissionID]
  );

  const localEmissionsRef = useRef(emissions);
  useEffect(() => { localEmissionsRef.current = localEmissions; }, [localEmissions]);

  const gridRef = useRef(null);
  const gridHeaderContainerRef = useRef(null);
  const inventoryDataRef = useRef({ selectedId: null, inventoryTotal: 0 });
  const prevSelectedEmissionIDRef = useRef(null);
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);
  const [focusedEmission, setFocusedEmission] = useState(null);
  const colVisibility_key = "emission_grid_columns";
  const isHidden = useCallback((field) => !columnVisibilities[colVisibility_key]?.includes(field), [columnVisibilities]);

  const [autoSaveInterval, setAutoSaveInterval] = useState(0);

  // Auto save interval options: Off (0), 30s (30000), 1min (60000), 5min (300000)
  const autoSaveOptions = [
    { value: 0, label: "Auto Save: Off" },
    { value: 30000, label: "Auto Save: 30s" },
    { value: 60000, label: "Auto Save: 1min" },
    { value: 300000, label: "Auto Save: 5min" },
  ];

  const handleAutoSaveClick = () => {
    const currentIndex = autoSaveOptions.findIndex((option) => option.value === autoSaveInterval);
    const nextIndex = (currentIndex + 1) % autoSaveOptions.length;
    setAutoSaveInterval(autoSaveOptions[nextIndex].value);
  };

  const getCurrentAutoSaveLabel = () => {
    const option = autoSaveOptions.find((opt) => opt.value === autoSaveInterval);
    return option ? option.label : "Auto Save: Off";
  };

  useEffect(() => {
    if (autoSaveInterval > 0) {
      const interval = setInterval(() => {
        setIsSaveClicked(true);
      }, autoSaveInterval);
      return () => clearInterval(interval);
    }
  }, [autoSaveInterval, setIsSaveClicked]);

  // Sync inventory total → write into gwp of the selected emission scope row
  useEffect(() => {
    // When selectedEmissionID just changed, inventoryTotalFossil still holds the PREVIOUS row's
    // value — OrganizationScope resets it in a separate useEffect that hasn't fired yet.
    // Skip the write here; the reset will trigger this effect again with inventoryTotalFossil=0.
    const selectionJustChanged = prevSelectedEmissionIDRef.current !== selectedEmissionID;
    prevSelectedEmissionIDRef.current = selectedEmissionID;

    inventoryDataRef.current = { selectedId: selectedEmissionID, inventoryTotal: inventoryTotalFossil };

    if (!selectionJustChanged && selectedEmissionID) {
      const currentRow = localEmissionsRef.current.find(e => e.id === selectedEmissionID);
      let needsSave = false;

      const isOutOfSync = currentRow && inventoryTotalFossil !== 0 && Math.abs((Number(currentRow.gwp) || 0) - inventoryTotalFossil) > 0.01;

      if (inventoryTotalFossil !== 0 && (hasPendingDetailEdits || isOutOfSync)) {
        setLocalEmissions(prev => prev.map(e =>
          e.id === selectedEmissionID && Math.abs((Number(e.gwp) || 0) - inventoryTotalFossil) > 0.01
            ? { ...e, gwp: inventoryTotalFossil, _isModified: true, _version: (e._version || 0) + 1 }
            : e
        ));
        needsSave = true;
      }

      if (inventoryTotalBiogenic !== 0 && (!currentRow || Math.abs((Number(currentRow.biogenic_gwp) || 0) - inventoryTotalBiogenic) > 0.01)) {
        if (hasPendingDetailEdits) {
          setLocalEmissions(prev => prev.map(e =>
            e.id === selectedEmissionID && e.biogenic_gwp !== inventoryTotalBiogenic
              ? { ...e, biogenic_gwp: inventoryTotalBiogenic, _isModified: true, _version: (e._version || 0) + 1 }
              : e
          ));
          needsSave = true;
        }
      }

      if (needsSave) setIsSaveVisible(true);
    }
    gridRef.current?.api?.refreshCells({ columns: ["gwp", "biogenic_gwp"], force: true });
  }, [selectedEmissionID, inventoryTotalFossil, inventoryTotalBiogenic, hasPendingDetailEdits, setIsSaveVisible]);

  // Synchronize local emissions with props, preserving modified and new unsaved rows
  useEffect(() => {
    setLocalEmissions((prevLocal) => {
      const isDirty = prevLocal.some(e => e._isModified || e._isNew) || emissionIdsToBeDeleted.length > 0;
      if (!isDirty) return sortByCategoryNumber(emissions);
      const deletedSet = new Set(emissionIdsToBeDeleted);
      const newRows = prevLocal.filter(e => e._isNew && !emissions.some(em => em.id === e.id));
      const merged = emissions
        .filter(em => !deletedSet.has(em.id))
        .map(propEm => {
          const localEm = prevLocal.find(l => l.id === propEm.id);
          return (localEm && localEm._isModified && !localEm._isNew) ? localEm : propEm;
        });
      return sortByCategoryNumber([...merged, ...newRows]);
    });
  }, [emissions, emissionIdsToBeDeleted]);

  useEffect(() => {
    if (!focusedEmission || !gridRef.current) return;
    setTimeout(() => {
      const api = gridRef.current?.api;
      if (!api) return;
      // Navigate to last page so the newly appended row is visible
      api.paginationGoToLastPage();
      setTimeout(() => {
        const node = api.getRowNode(String(focusedEmission.id));
        if (node) {
          api.ensureIndexVisible(node.rowIndex, "bottom");
          node.setSelected(true, true);
          api.startEditingCell({ rowIndex: node.rowIndex, colKey: "category" });
          setFocusedEmission(null);
        }
      }, 50);
    }, 100);
  }, [localEmissions, focusedEmission]);

  const newEmission = useCallback(async () => {
    if (readOnly) return;
    const supply_chain_id = organizationType === Supply_chain.label ? organizationID : null;
    const corp_id = organizationType === Corporate.label ? organizationID : null;
    const structure_id = organizationType === Structure.label ? organizationID : null;
    if (!supply_chain_id && !corp_id && !structure_id) return;

    let defaultCategory = "";
    let is_avoided = 0;
    let is_biogenic = 0;

    if (selectedTab === "Scope 1") {
      defaultCategory = scope1Categories[0] || "";
    } else if (selectedTab === "Scope 2") {
      defaultCategory = scope2Categories[0] || "";
    } else if (selectedTab === "Scope 3") {
      defaultCategory = scope3Categories[0] || "";
    } else if (selectedTab === "Avoided") {
      is_avoided = 1;
      defaultCategory = emissionCategories?.[0] || "";
    } else if (selectedTab === "Biogenic") {
      is_biogenic = 1;
      defaultCategory = emissionCategories?.[0] || "";
    } else if (emissionCategories && emissionCategories.length > 0) {
      defaultCategory = emissionCategories[0];
    }

    const newId = uuidv4().replace(/-/g, "");
    const newRow = {
      id: newId,
      category: defaultCategory,
      description: "",
      gwp: 0,
      biogenic_gwp: 0,
      is_avoided,
      is_biogenic,
      supply_chain_id,
      structure_id,
      corp_id,
      _isNew: true,
      _isModified: true,
    };
    setLocalEmissions((prev) => sortByCategoryNumber([...prev, newRow]));
    setFocusedEmission(newRow);
    setSelectedEmissionIDs([newId]);
    setIsSaveVisible(true);
  }, [readOnly, organizationType, organizationID, selectedTab, emissionCategories, setSelectedEmissionIDs, setIsSaveVisible]);

  const deleteSelected = useCallback(() => {
    if (readOnly || selectedRowIDs.length === 0) return;
    const newRowIDs = new Set(localEmissions.filter(e => e._isNew).map(e => e.id));
    const savedIDs = selectedRowIDs.filter(id => !newRowIDs.has(id));

    setLocalEmissions(prev => prev.filter(e => !selectedRowIDs.includes(e.id)));
    if (savedIDs.length > 0) {
      setEmissionIdsToBeDeleted(prev => [...prev, ...savedIDs]);
    }
    setSelectedRowIDs([]);
    setSelectedEmissionIDs([]);
    setIsSaveVisible(true);
  }, [readOnly, selectedRowIDs, localEmissions, setSelectedEmissionIDs, setIsSaveVisible]);

  const rawColumnDefs = useMemo(() => [
    { field: "scope", headerName: "Scope", valueGetter: (p) => p.data, cellRenderer: ScopeBadgeRenderer, ...defaultColDef, ...centerAlignColDef, cellStyle: { ...centerAlignColDef.cellStyle, display: "flex", alignItems: "center", justifyContent: "center" }, flex: 0.6, resizable: true, suppressMovable: false, headerCheckboxSelection: true, checkboxSelection: false },
    {
      field: "category",
      headerName: "Scope Category",
      valueGetter: (params) => {
        const val = params.data?.category;
        if (!val) return "";
        if (typeof val === "object") {
          return val.name || val.label || val.category || "";
        }
        return String(val);
      },
      valueSetter: (params) => {
        params.data.category = params.newValue;
        return true;
      },
      ...defaultColDef,
      ...leftAlignColDef,
      cellStyle: { ...leftAlignColDef.cellStyle, paddingLeft: "8px" },
      flex: 1,
      editable: !readOnly,
      cellEditor: GridSelectEditor,
      cellEditorParams: () => ({ values: emissionCategories }),
      resizable: true,
    },
    { field: "description", headerName: "Description", ...defaultColDef, ...leftAlignColDef, cellStyle: { ...leftAlignColDef.cellStyle, paddingLeft: "8px" }, flex: 1, editable: !readOnly, resizable: true },
    { field: "gwp", headerName: "GWP (kg CO₂e)", ...defaultColDef, ...rightAlignColDef, ...floatFormater, editable: !readOnly, flex: 1, resizable: true },
    { field: "biogenic_gwp", headerName: "Biogenic GWP (kg CO₂e)", ...defaultColDef, ...rightAlignColDef, ...floatFormater, editable: !readOnly, flex: 1, resizable: true },
    { field: "is_avoided", headerName: "Avoided", ...defaultColDef, ...centerAlignColDef, cellRenderer: CheckboxRenderer, editable: false, flex: 1, resizable: true },
    { field: "is_biogenic", headerName: "Biogenic", ...defaultColDef, ...centerAlignColDef, cellRenderer: CheckboxRenderer, editable: false, flex: 1, resizable: true },
    {
      field: "period_type",
      headerName: "Period Type",
      ...defaultColDef,
      ...centerAlignColDef,
      width: 125,
      minWidth: 110,
      editable: !readOnly,
      resizable: true,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: ["Monthly", "Quarterly", "Annual", "Bimonthly", "Custom"]
      },
      cellRenderer: (params) => {
        const val = params.value;
        if (!val || val === "N/A") {
          return <span style={{ color: "var(--m3-outline, #94a3b8)", fontSize: "12px" }}>-</span>;
        }
        const isQ = String(val).toLowerCase().includes("quarter");
        const isA = String(val).toLowerCase().includes("annual");
        const bg = isQ ? "rgba(99, 102, 241, 0.12)" : isA ? "rgba(168, 85, 247, 0.12)" : "rgba(34, 197, 94, 0.12)";
        const color = isQ ? "#4f46e5" : isA ? "#7e22ce" : "#16a34a";
        return (
          <span style={{
            display: "inline-block",
            padding: "2px 8px",
            borderRadius: "6px",
            fontSize: "11px",
            fontWeight: 600,
            backgroundColor: bg,
            color: color,
          }}>
            {val}
          </span>
        );
      }
    },
    {
      field: "billing_period_start",
      headerName: "Period Start",
      ...defaultColDef,
      ...centerAlignColDef,
      width: 155,
      minWidth: 140,
      editable: !readOnly,
      resizable: true,
      cellEditor: GridDateEditor,
      cellRenderer: M3DateCellRenderer,
    },
    {
      field: "billing_period_end",
      headerName: "Period End",
      ...defaultColDef,
      ...centerAlignColDef,
      width: 155,
      minWidth: 140,
      editable: !readOnly,
      resizable: true,
      cellEditor: GridDateEditor,
      cellRenderer: M3DateCellRenderer,
    },
    {
      field: "meter_number",
      headerName: "Meter Number",
      ...defaultColDef,
      ...leftAlignColDef,
      width: 135,
      minWidth: 110,
      editable: !readOnly,
      resizable: true,
      valueFormatter: (params) => params.value || "-"
    },
    {
      field: "total_cost",
      headerName: "Total Cost",
      ...defaultColDef,
      ...rightAlignColDef,
      width: 135,
      minWidth: 110,
      editable: !readOnly,
      resizable: true,
      valueFormatter: (params) => {
        if (params.value == null || params.value === "") return "-";
        const val = Number(params.value);
        if (isNaN(val)) return "-";
        const curr = params.data?.currency ? ` ${params.data.currency}` : "";
        return `${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${curr}`;
      },
      valueParser: (params) => (params.newValue !== "" && !isNaN(params.newValue) ? parseFloat(params.newValue) : null),
    },
    {
      field: "currency",
      headerName: "Currency",
      ...defaultColDef,
      ...centerAlignColDef,
      width: 105,
      minWidth: 90,
      editable: !readOnly,
      resizable: true,
      cellEditor: "agSelectCellEditor",
      cellEditorParams: {
        values: ["CAD", "USD", "EUR", "GBP", "INR", "AUD"]
      },
      valueFormatter: (params) => params.value || "-"
    },
    {
      field: "notes",
      headerName: "Notes",
      ...defaultColDef,
      ...leftAlignColDef,
      width: 220,
      minWidth: 160,
      editable: !readOnly,
      resizable: true,
      cellEditor: "agTextCellEditor"
    },
  ], [readOnly, emissionCategories]);

  const columnDefs = useMemo(() => rawColumnDefs.map(c => ({ ...c, hide: isHidden(c.field) })), [rawColumnDefs, isHidden]);
  const defaultCols = useMemo(() => rawColumnDefs.map(c => c.field).filter(f => f !== "is_avoided" && f !== "is_biogenic"), [rawColumnDefs]);

  const exportData = useMemo(() => {
    const isSupplyChain = organizationType === Supply_chain.label;
    return localEmissions.map((row) => ({
      Scope: getScopeLabel(row),
      "Scope Category": typeof row.category === "object" ? (row.category?.name || row.category?.label || "") : (row.category || ""),
      Description: row.description,
      "GWP (kg CO2e)": row.gwp,
      "Biogenic GWP (kg CO2e)": row.biogenic_gwp,
      "Period Type": row.period_type || "-",
      "Period Start": row.billing_period_start ? (dayjs(String(row.billing_period_start).replace(/\s+00:00:00.*$/, "").trim()).isValid() ? dayjs(String(row.billing_period_start).replace(/\s+00:00:00.*$/, "").trim()).format("YYYY-MM-DD") : row.billing_period_start) : "-",
      "Period End": row.billing_period_end ? (dayjs(String(row.billing_period_end).replace(/\s+00:00:00.*$/, "").trim()).isValid() ? dayjs(String(row.billing_period_end).replace(/\s+00:00:00.*$/, "").trim()).format("YYYY-MM-DD") : row.billing_period_end) : "-",
      "Meter Number": row.meter_number || "-",
      "Total Cost": row.total_cost != null ? row.total_cost : "-",
      Currency: row.currency || "-",
      Notes: row.notes || "-",
      Avoided: !!row.is_avoided,
      Biogenic: !!row.is_biogenic,
      ...(isSupplyChain
        ? { supply_chain_id: row.supply_chain_id, id: row.id }
        : { structure_id: row.structure_id, id: row.id }),
    }));
  }, [localEmissions, organizationType]);

  // Expose save handlers to parent (OrganizationScope) via saveRef
  useEffect(() => {
    if (saveRef) {
      saveRef.current = {
        stopEditing: () => {
          gridRef.current?.api?.stopEditing();
        },
        getSavePayload: () => {
          const currentList = localEmissionsRef.current || localEmissions;
          const toSave = currentList
            .filter((e) => e._isModified || e._isNew)
            .map((e) => {
              const { _version, ...clean } = e;
              return {
                ...clean,
                gwp: (clean.gwp !== null && clean.gwp !== undefined && clean.gwp !== "") ? parseFloat(clean.gwp) : 0.0,
                biogenic_gwp: (clean.biogenic_gwp !== null && clean.biogenic_gwp !== undefined && clean.biogenic_gwp !== "") ? parseFloat(clean.biogenic_gwp) : 0.0,
                total_cost: (clean.total_cost !== null && clean.total_cost !== undefined && clean.total_cost !== "") ? parseFloat(clean.total_cost) : null,
                currency: clean.currency || null,
                billing_period_start: clean.billing_period_start || null,
                billing_period_end: clean.billing_period_end || null,
                period_type: clean.period_type || null,
                meter_number: clean.meter_number || null,
                notes: clean.notes || null,
                is_avoided: clean.is_avoided ? 1 : 0,
                is_biogenic: clean.is_biogenic ? 1 : 0,
                is_new: Boolean(clean._isNew),
                is_modified: Boolean(clean._isModified),
              };
            });
          return {
            toSave,
            toDelete: emissionIdsToBeDeleted,
          };
        },
        onSaveSuccess: (payload) => {
          const savedIds = new Set(payload.toSave.map((e) => e.id));
          const deletedIds = new Set(payload.toDelete);
          setLocalEmissions((prev) =>
            prev
              .filter((e) => !deletedIds.has(e.id))
              .map((e) => {
                if (savedIds.has(e.id)) {
                  const { _isNew, _isModified, ...clean } = e;
                  return clean;
                }
                return e;
              })
          );
          setEmissionIdsToBeDeleted([]);
        },
      };
    }
  }, [saveRef, localEmissions, emissionIdsToBeDeleted]);

  const onSelectionChanged = useCallback((e) => {
    const selected = e.api.getSelectedRows();
    const ids = selected.map(r => r.id);
    setSelectedRowIDs(ids);
    setSelectedEmissionIDs(ids);
  }, [setSelectedEmissionIDs]);

  const onCellValueChanged = useCallback((p) => {
    const { colDef, data, node, api } = p;
    let newGwp = data.gwp;
    if (colDef.field === "is_avoided" || colDef.field === "is_biogenic" || colDef.field === "gwp") {
      if (data.is_avoided || data.is_biogenic) {
        newGwp = -Math.abs(data.gwp || 0);
      } else {
        newGwp = Math.abs(data.gwp || 0);
      }
      data.gwp = newGwp;
      api?.refreshCells({ force: true, rowNodes: [node] });
    }
    setLocalEmissions((prev) =>
      prev.map((e) => {
        if (e.id === data.id) {
          return {
            ...e,
            ...data,
            gwp: newGwp,
            _isModified: true,
            _version: (e._version || 0) + 1,
          };
        }
        return e;
      })
    );
    setIsSaveVisible(true);
  }, [setIsSaveVisible]);


  const totalFossil = useMemo(() => localEmissions.reduce((sum, item) => !item.is_avoided && !item.is_biogenic ? sum + (Number(item.gwp) || 0) : sum, 0), [localEmissions]);
  const totalBiogenic = useMemo(() => localEmissions.reduce((sum, item) => sum + (Number(item.biogenic_gwp) || 0), 0), [localEmissions]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const count = localEmissions.length;
    const currentSize = api.paginationGetPageSize();
    const needed = PAGE_STEPS.find(s => s >= count) ?? count;
    if (needed > currentSize) api.setGridOption("paginationPageSize", needed);
  }, [localEmissions.length]);

  const footerRight = (
    <Box sx={{ display: "flex", gap: 3, alignItems: "center", pr: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? "#a1a1aa" : "#64748b" }}>
        Total Biogenic: <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: totalBiogenic < 0 ? "#659d33" : (isDark ? "#ffffff" : "#0f172a") }}>{totalBiogenic.toLocaleString("en-CA", { maximumFractionDigits: 2 })}</Typography>
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? "#a1a1aa" : "#64748b" }}>
        Total Fossil: <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: totalFossil < 0 ? "#659d33" : (isDark ? "#ffffff" : "#0f172a") }}>{totalFossil.toLocaleString("en-CA", { maximumFractionDigits: 2 })}</Typography>
      </Typography>
    </Box>
  );

  const handleExportXLSX = useCallback(() => {
    if (!exportData || exportData.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Emissions");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, "emissions_data.xlsx");
  }, [exportData]);

  const addAnalyzeOptions = useMemo(() => [
    {
      key: "add-manual",
      label: t("Add Manual Row"),
      description: t("Insert a blank emission row into the grid"),
      icon: <FiPlus size={16} />,
      disabled: readOnly,
      onClick: newEmission,
    },
    { divider: true },
    {
      key: "analyze-bills",
      label: t("Analyze Invoices & Bills"),
      description: selectedEmission
        ? t("Analyze invoices & bills for selected row: {{desc}}", {
            desc:
              selectedEmission.description ||
              (typeof selectedEmission.category === "object"
                ? selectedEmission.category?.name
                : selectedEmission.category),
          })
        : t("Extract data from utility bills & vendor invoices"),
      icon: <LuFileText size={16} />,
      disabled: readOnly,
      onClick: () => {
        if (readOnly) {
          enqueueAlert?.({
            message: t(`Cannot upload or edit: Inventory is currently ${validationStatus || "locked"}.`),
            severity: "warning",
          });
          return;
        }
        setBatchAnalyzerOpen(true);
      },
    },
    {
      key: "upload-report",
      label: t("Previous Report"),
      description: t("Extract disclosures from previous reports"),
      icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} />,
      disabled: readOnly,
      onClick: () => {
        if (readOnly) {
          enqueueAlert?.({
            message: t(`Cannot upload or edit: Inventory is currently ${validationStatus || "locked"}.`),
            severity: "warning",
          });
          return;
        }
        setUploadOpen(true);
      },
    },
  ], [t, readOnly, selectedEmission, newEmission, enqueueAlert, validationStatus]);

  return (
    <>
      <DataTableCard
        title={organizationType === Supply_chain.label ? t("Emissions data - Scopes") : t("Scopes Emissions Data")}
        leading={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, ml: { xs: 0, md: 2 }, flexWrap: "nowrap" }}>
            <Chip
              size="md"
              tone={autoSaveInterval > 0 ? "primary" : "neutral"}
              icon={<BsClock style={{ fontSize: "14px" }} />}
              onClick={handleAutoSaveClick}
              tooltip={t("Toggle auto save interval")}
            >
              {getCurrentAutoSaveLabel()}
            </Chip>
            <SegmentedButton
              size="sm"
              showCheck={false}
              options={(tabs || []).map((tab) => ({
                value: tab.value,
                label: t(tab.label),
              }))}
              value={selectedTab}
              onChange={(val) => setSelectedTab(val)}
              ariaLabel={t("Scope filter")}
            />
          </Box>
        }
        actions={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "nowrap" }}>
            <SplitButton
              size="sm"
              variant="filled"
              icon={<FiPlus size={16} />}
              label={t("Add / Analyze")}
              onClick={newEmission}
              disabled={readOnly}
              options={addAnalyzeOptions}
            />
            <Tooltip title={t("Manage Columns")}>
              <span ref={gridHeaderContainerRef} style={{ display: "inline-flex" }}>
                <M3IconButton
                  size="sm"
                  icon={<LuFilter size={16} />}
                  onClick={() => setColumnMenuVisible((p) => !p)}
                  aria-label={t("Manage Columns")}
                  style={{
                    background: "var(--m3-primary-container)",
                    color: "var(--m3-on-primary-container)",
                  }}
                />
              </span>
            </Tooltip>
            <Tooltip title={t("Remove Selected")}>
              <span>
                <M3IconButton
                  size="sm"
                  icon={<FiMinus size={18} />}
                  onClick={deleteSelected}
                  disabled={readOnly || selectedRowIDs.length === 0}
                  aria-label={t("Remove Selected")}
                  style={{
                    background: (readOnly || selectedRowIDs.length === 0) ? "rgba(239, 68, 68, 0.06)" : "rgba(239, 68, 68, 0.15)",
                    color: (readOnly || selectedRowIDs.length === 0) ? "rgba(239, 68, 68, 0.35)" : "#dc2626",
                  }}
                />
              </span>
            </Tooltip>
            <Tooltip title={t("Download XLSX")}>
              <span>
                <M3IconButton
                  size="sm"
                  icon={<LuDownload size={16} />}
                  onClick={handleExportXLSX}
                  aria-label={t("Download XLSX")}
                  style={{
                    background: "var(--m3-primary-container)",
                    color: "var(--m3-on-primary-container)",
                  }}
                />
              </span>
            </Tooltip>
          </Box>
        }
      >
        <AgGrid
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={localEmissions}
          onSelectionChanged={onSelectionChanged}
          headerCheckbox={false}
          checkboxes={false}
          rowClickToggle={true}
          domLayout="autoHeight"
          pageSizeOptions={[15, 25, 50, 100, "All"]}
          footerRightContent={footerRight}
          onCellValueChanged={onCellValueChanged}
          sx={{
            '& .ag-header-cell[col-id="scope"] .ag-header-cell-label': {
              justifyContent: "center",
            },
          }}
        />
      </DataTableCard>
      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={rawColumnDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
      />
      <OrganizationDocumentUpload
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        organizationId={organizationID}
        organizationType={organizationType}
        onDataSaved={() => {
          setRefresh((r) => !r);
          setUploadOpen(false);
        }}
      />
      <EvidenceBillBatchAnalyzerModal
        open={batchAnalyzerOpen}
        onClose={() => setBatchAnalyzerOpen(false)}
        selectedRow={selectedEmission}
        scope={selectedEmission ? getScopeLabel(selectedEmission) : (selectedTab && selectedTab !== "All" ? selectedTab : "Scope 1")}
        organizationID={organizationID}
        organizationType={organizationType}
        pageType={
          organizationType === "Supply_chain"
            ? "supply_chain"
            : organizationType === "Corporate"
            ? "corporate"
            : "organization"
        }
        onSuccess={(resData) => {
          setRefresh?.((r) => !r);
          const targetParentId = resData?.parent_emission_id || selectedEmission?.id;
          const newGwp = resData?.total_gwp;
          if (targetParentId) {
            setLocalEmissions((prev) => {
              const exists = prev.some((e) => e.id === targetParentId || (selectedEmission && e.id === selectedEmission.id));
              if (exists) {
                return prev.map((e) =>
                  e.id === targetParentId || (selectedEmission && e.id === selectedEmission.id)
                    ? {
                        ...e,
                        id: targetParentId,
                        gwp: newGwp != null ? newGwp : e.gwp,
                        _isNew: false,
                        _isModified: false,
                      }
                    : e
                );
              }
              const newRow = {
                id: targetParentId,
                category: resData?.category || selectedEmission?.category || "Stationary combustion",
                description: resData?.description || "",
                gwp: newGwp != null ? newGwp : 0,
                biogenic_gwp: 0,
                is_avoided: 0,
                is_biogenic: 0,
                structure_id: organizationType === "Structure" ? organizationID : null,
                corp_id: organizationType === "Corporate" ? organizationID : null,
                supply_chain_id: organizationType === "Supply_chain" ? organizationID : null,
                _isNew: false,
                _isModified: false,
              };
              return [newRow, ...prev];
            });
            setSelectedRowIDs([targetParentId]);
            setSelectedEmissionIDs([targetParentId]);
            setTimeout(() => {
              gridRef.current?.api?.forEachNode((node) => {
                if (node.data?.id === targetParentId) {
                  node.setSelected(true, true);
                }
              });
              gridRef.current?.api?.refreshCells({ force: true });
            }, 100);
          }
          setRefresh((r) => !r);
          setBatchAnalyzerOpen(false);
        }}
      />
    </>
  );
};

export default EmissionGrid;
